import utils from '@bigcommerce/stencil-utils';
import { showAlertModal } from '../modal';
import { normalizeMultiplier, parseNumber, parsePriceText } from './helpers';
import { pulseBadgeForQuantityChange, revealBadge, setBadgeContent } from './render';

const QUICK_CART_BADGE_SELECTOR = '[data-quick-cart-badge="true"]';
const QUICK_CART_QTY_INPUT_SELECTOR = '[data-quick-cart-qty-input]';
const QUICK_CART_QTY_CONTROL_SELECTOR = '[data-quick-cart-quantity-change]';
const QUICK_CART_QTY_DISPLAY_SELECTOR = '[data-quick-cart-qty-display]';
const QUICK_CART_BOUND_ATTR = 'data-quick-cart-bound';

function readQuantity(input) {
    return Math.max(1, Math.round(parseNumber(input?.value)) || 1);
}

function readMin(input) {
    return Math.max(1, Math.round(parseNumber(input?.dataset.quantityMin)) || 1);
}

function readMax(input) {
    const parsed = Math.round(parseNumber(input?.dataset.quantityMax));
    return Number.isNaN(parsed) || parsed <= 0 ? 0 : parsed;
}

function buildMultiplierQuery(path) {
    return `query QuickCartEntryMultiplier {
        site {
            route(path: ${JSON.stringify(path)}) {
                node {
                    ... on Product {
                        customFields {
                            edges {
                                node {
                                    name
                                    value
                                }
                            }
                        }
                    }
                }
            }
        }
    }`;
}

async function fetchMultiplierByPath(path, storefrontApiToken) {
    if (!path || !storefrontApiToken) {
        return NaN;
    }

    const response = await fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            Authorization: `Bearer ${storefrontApiToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            query: buildMultiplierQuery(path),
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed with status ${response.status}`);
    }

    const payload = await response.json();
    const customFieldEdges = payload?.data?.site?.route?.node?.customFields?.edges || [];
    const entryMultiplierNode = customFieldEdges.find((edge) => edge?.node?.name === 'entry_multiplier');

    return parseNumber(entryMultiplierNode?.node?.value);
}

export function initQuickCartPreview({
    modal,
    context,
    multiplierCache,
    saveCache,
    refreshCartContent,
    cartItemId,
}) {
    const root = modal?.$content?.get(0);

    if (!root) {
        return;
    }

    const badge = root.querySelector(QUICK_CART_BADGE_SELECTOR);
    const qtyInput = root.querySelector(QUICK_CART_QTY_INPUT_SELECTOR);
    const qtyControl = root.querySelector(QUICK_CART_QTY_CONTROL_SELECTOR);
    const qtyDisplay = root.querySelector(QUICK_CART_QTY_DISPLAY_SELECTOR);

    if (!badge || !qtyInput) {
        return;
    }

    const unitPrice = parsePriceText(badge.dataset.entriesPriceText || '');

    if (Number.isNaN(unitPrice) || unitPrice <= 0) {
        return;
    }

    let multiplier = normalizeMultiplier(parseNumber(badge.dataset.entriesMultiplier));
    let lastRenderedQuantity = null;

    function render(quantity, shouldPulse = false) {
        const qty = Math.max(readMin(qtyInput), Math.round(quantity) || readMin(qtyInput));
        const perUnitEntries = unitPrice * multiplier;
        const totalEntries = perUnitEntries * qty;

        setBadgeContent(badge, {
            totalEntries,
            multiplier,
            entriesText: `${Math.round(totalEntries).toLocaleString()} TOTAL ENTRIES`,
            multiplierText: `${Math.round(perUnitEntries).toLocaleString()} x ${qty}`,
        });

        if (qtyDisplay) {
            qtyDisplay.textContent = qty;
        }

        if (shouldPulse && lastRenderedQuantity !== qty) {
            pulseBadgeForQuantityChange(badge);
        }

        lastRenderedQuantity = qty;
    }

    function showInputError(message) {
        if (!message) {
            return;
        }

        showAlertModal(message);
    }

    function validateQuantity(nextQty) {
        const minQty = readMin(qtyInput);
        const maxQty = readMax(qtyInput);

        if (!nextQty || Number.isNaN(nextQty)) {
            showInputError(context.productQuantityErrorMessage || 'Please enter a valid quantity.');
            qtyInput.value = lastRenderedQuantity || minQty;
            render(readQuantity(qtyInput), false);
            return null;
        }

        if (nextQty < minQty) {
            showInputError(qtyInput.dataset.quantityMinError);
            qtyInput.value = lastRenderedQuantity || minQty;
            render(readQuantity(qtyInput), false);
            return null;
        }

        if (maxQty > 0 && nextQty > maxQty) {
            showInputError(qtyInput.dataset.quantityMaxError);
            qtyInput.value = lastRenderedQuantity || minQty;
            render(readQuantity(qtyInput), false);
            return null;
        }

        return nextQty;
    }

    function persistQuantity(nextQty) {
        const validatedQty = validateQuantity(nextQty);

        if (validatedQty === null) {
            return;
        }

        modal.pending = true;
        utils.api.cart.itemUpdate(cartItemId, validatedQty, (err, response) => {
            modal.pending = false;

            if (err || response?.data?.status !== 'succeed') {
                const errorText = err || response?.data?.errors?.join('\n') || 'Unable to update quantity.';
                showInputError(errorText);
                qtyInput.value = lastRenderedQuantity || readMin(qtyInput);
                render(readQuantity(qtyInput), false);
                return;
            }

            refreshCartContent(cartItemId);
        });
    }

    function bindQuantityEvents() {
        if (!qtyInput.hasAttribute(QUICK_CART_BOUND_ATTR)) {
            qtyInput.addEventListener('input', () => {
                const typedQty = parseNumber(qtyInput.value);

                if (Number.isNaN(typedQty) || typedQty <= 0) {
                    return;
                }

                render(typedQty, true);
            });

            qtyInput.addEventListener('keyup', () => {
                const typedQty = parseNumber(qtyInput.value);

                if (Number.isNaN(typedQty) || typedQty <= 0) {
                    return;
                }

                render(typedQty, true);
            });

            qtyInput.addEventListener('change', () => {
                persistQuantity(readQuantity(qtyInput));
            });

            qtyInput.setAttribute(QUICK_CART_BOUND_ATTR, 'true');
        }

        if (qtyControl && !qtyControl.hasAttribute(QUICK_CART_BOUND_ATTR)) {
            qtyControl.addEventListener('click', (event) => {
                const button = event.target.closest('button[data-quick-cart-update]');

                if (!button) {
                    return;
                }

                event.preventDefault();

                const currentQty = readQuantity(qtyInput);
                const minQty = readMin(qtyInput);
                const maxQty = readMax(qtyInput);
                let nextQty = button.dataset.action === 'inc' ? currentQty + 1 : currentQty - 1;

                if (nextQty < minQty) {
                    nextQty = minQty;
                }

                if (maxQty > 0 && nextQty > maxQty) {
                    nextQty = maxQty;
                }

                qtyInput.value = nextQty;
                render(nextQty, true);
                persistQuantity(nextQty);
            });

            qtyControl.setAttribute(QUICK_CART_BOUND_ATTR, 'true');
        }
    }

    function initializeResolvedMultiplier() {
        render(readQuantity(qtyInput), false);
        revealBadge(badge, { pulse: false });
        bindQuantityEvents();
    }

    const productPath = badge.dataset.productPath || '';

    if (!badge.dataset.entriesMultiplier && productPath && multiplierCache.has(productPath)) {
        multiplier = normalizeMultiplier(multiplierCache.get(productPath));
    }

    if (!badge.dataset.entriesMultiplier && productPath && !multiplierCache.has(productPath) && context.storefrontApiToken) {
        fetchMultiplierByPath(productPath, context.storefrontApiToken)
            .then((value) => {
                multiplier = normalizeMultiplier(value);
                multiplierCache.set(productPath, multiplier);
                saveCache();
                initializeResolvedMultiplier();
            })
            .catch(() => {
                initializeResolvedMultiplier();
            });

        return;
    }

    initializeResolvedMultiplier();
}
